/**
 * 家长端助手的 agent loop（网页版只有文字对话，与 iOS VoiceAgent + ParentToolbox 一致，去掉语音部分）：
 * 输入 → 模型（流式，带 MCP 工具）→ 需要时调用工具、把结果交回模型，循环到得出答复（最多 10 步）。
 * - 模型接口由浏览器直接调用（chat.ts），工具走同源 /mcp；
 * - 不可逆操作（destructiveHint）执行前用 confirm() 请家长确认；本轮有写操作时触发同步；
 * - 会话历史存在服务端（docs/语音助手.md 第 4 部分），上下文 = 摘要 + 预算内的最近消息。
 * 状态放在模块级 store 里，切换页面后对话还在；退出登录时清空。
 */
import { answerConfirm, api, ApiError, assetUrl, calendarNow, confirm, createStore, dataStore, select, sessionStore, syncNow, syncSoon, toast, upload, useStoreOf, WEEKDAY_NAMES } from "../../core";
import { ChatError, streamChat, type ToolCall } from "./chat";
import {
  contextBudget,
  describeArgs,
  displayText,
  keepFrom,
  MAX_HISTORY,
  messageTokens,
  requestMessages,
  savedUserText,
  toContextMessage,
  withSummary,
  type AttachmentMeta,
  type ContextMessage,
  type ServerMessage,
  type ToolCallJSON,
} from "./context";
import { McpClient } from "./mcp";
import { openPdfData, pdfPageText, type PreparedAttachment } from "./attachments";
import { READ_ATTACHMENT_TOOL, readAttachment, type ReadDeps, type SentAttachment } from "./read-attachment";
import { supportsThinking, thinksEveryStep, visionRoute, type ChatMessage, type ToolSpec } from "./providers";
import { chatRoute, clearCredentials, credStore, prefsStore, refreshCredentials } from "./settings";

export type ToolState = "running" | "done" | "failed" | "cancelled";

export type Item =
  | { id: string; kind: "user"; text: string }
  | { id: string; kind: "assistant"; text: string }
  | { id: string; kind: "thinking"; text: string }
  | { id: string; kind: "tool"; title: string; state: ToolState }
  | { id: string; kind: "error"; text: string };

export interface AgentState {
  items: Item[];
  /** 正在处理（可以点停止） */
  busy: boolean;
  /** 正在思考的条目（展开显示） */
  liveThinkingId: string | null;
  historyLoaded: boolean;
  loadingHistory: boolean;
  /** 服务端还有更早的记录 */
  hasOlder: boolean;
  loadingOlder: boolean;
  /** 正在上传附件 */
  uploading: boolean;
}

const initialState = (): AgentState => ({
  items: [],
  busy: false,
  liveThinkingId: null,
  historyLoaded: false,
  loadingHistory: false,
  hasOlder: false,
  loadingOlder: false,
  uploading: false,
});

export const agentStore = createStore<AgentState>(initialState());

export function useAgent<T>(selector: (s: AgentState) => T, isEqual?: (a: T, b: T) => boolean): T {
  return useStoreOf(agentStore, selector, isEqual);
}

const MAX_STEPS = 10;
const MAX_TOOL_RESULT = 12_000;
const MISSING_KEY = "还没有设置百炼 API Key，请到「助手设置」里填写。";

interface ToolDef {
  name: string;
  title: string;
  description: string;
  parameters: unknown;
  readOnly: boolean;
  destructive: boolean;
}

/** 检索聊天记录（MCP 工具列表之外另加的本地工具） */
const SEARCH_TOOL: ToolDef = {
  name: "search_history",
  title: "查聊天记录",
  description: "在以前的对话记录里全文检索。需要更早的对话细节（之前安排过什么、说过什么、导入过什么文件）时用。query 用一两个关键词，多个用空格分开。",
  parameters: {
    type: "object",
    properties: { query: { type: "string", description: "关键词" }, limit: { type: "integer", description: "最多返回几条，默认 5" } },
    required: ["query"],
  },
  readOnly: true,
  destructive: false,
};

/** 现在的日期和时间（读这台电脑 / 手机的时间，按家庭时区换算；本地工具） */
const NOW_TOOL: ToolDef = {
  name: "get_now",
  title: "查现在时间",
  description: "获取现在的日期、星期和时间（按家庭时区）。安排今天的事、设提醒前先查现在几点，不要安排在已经过去的时间。",
  parameters: { type: "object", properties: {} },
  readOnly: true,
  destructive: false,
};

function nowResult(): { text: string; isError: boolean } {
  const cal = calendarNow();
  const today = cal.today();
  const tz = dataStore.getState().family?.timezone ?? "Asia/Shanghai";
  return { text: JSON.stringify({ date: today, weekday: `星期${WEEKDAY_NAMES[cal.weekday(today)]}`, time: cal.timeString(Date.now()), timezone: tz, epoch_ms: Date.now() }), isError: false };
}

// ---------- 模块内部状态（不需要触发界面刷新） ----------

let mcp = new McpClient();
let tools = new Map<string, ToolDef>();
let toolSpecs: ToolSpec[] = [];
/** 发给模型的上下文（摘要之后的最近消息） */
let context: ContextMessage[] = [];
/** 还没保存到服务端的消息（服务端格式） */
let unsaved: ServerMessage[] = [];
let summary: string | null = null;
let summaryUptoSeq = 0;
let oldestSeq: number | null = null;
let flushing = false;
let abort: AbortController | null = null;
/** 每次 reset 加一，丢弃旧请求的结果 */
let generation = 0;

function uid(): string {
  return crypto.randomUUID().toLowerCase();
}

function setItems(fn: (items: Item[]) => Item[]): void {
  agentStore.setState((s) => ({ items: fn(s.items) }));
}

/** 去掉 id 的条目（对联合类型逐个 Omit） */
type WithoutId<T> = T extends unknown ? Omit<T, "id"> : never;
type NewItem = WithoutId<Item>;

function append(item: NewItem): string {
  const id = uid();
  setItems((items) => [...items, { ...item, id } as Item]);
  return id;
}

function patchItem(id: string, patch: Partial<Item>): void {
  setItems((items) => items.map((it) => (it.id === id ? ({ ...it, ...patch } as Item) : it)));
}

function errorText(e: unknown): string {
  if (e instanceof ChatError) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}

function isAbort(e: unknown): boolean {
  return (e as Error)?.name === "AbortError";
}

// ---------- 系统提示 ----------

function systemPrompt(): string {
  const s = dataStore.getState();
  const cal = calendarNow();
  const today = cal.today();
  const weekday = WEEKDAY_NAMES[cal.weekday(today)];
  const kids = select.sortedChildren(s).map((c) => `${c.name}（id: ${c.id}）`);
  const me = s.me?.displayName || "家长";
  return [
    `你是「家庭学习平台」家长端网页版里的助手，帮家长（${me}）管理孩子的时间表、提醒、学习内容和安排、积分、卡牌和奖品兑换。`,
    `今天是 ${today}（星期${weekday}），现在是 ${cal.timeString(Date.now())}，时区 ${s.family?.timezone ?? "Asia/Shanghai"}。孩子：${kids.length ? kids.join("、") : "还没有添加"}。`,
    "规则：",
    "1. 涉及家庭数据时一定用工具查询或修改，不要编造；需要日期时按上面的今天推算，用 YYYY-MM-DD。上面已经给出今天的日期和孩子的 id，不需要再调用 get_family。",
    "2. 回复显示在网页的对话里：一般用简短口语化的中文，一两句话；内容较多（多条日程、步骤、对比）或家长要求时，可以用 Markdown（列表、表格、加粗）。不要用表情。",
    "3. 信息不全又无法合理推断时，先简短追问一句；能合理推断的（比如只有一个孩子、常见时长）直接做。",
    "4. 修改完成后简单说明做了什么；工具返回错误时根据错误修正参数重试一次，仍失败就告诉家长原因。",
    "5. 删除、扣分等不可逆操作会由页面弹窗请家长确认，你直接调用工具即可；家长取消后不要再重试。",
    "6. 家长发来的图片、文件内容会附在消息里（比如课程表），按家长的要求整理或导入。附件已经上传，消息里的「附件 id」可以直接当 asset_id 用（比如 PDF 布置成阅读内容：create_contents 的 activity_type=pdf_reader，payload.pdf_asset_id 填它，不用再 create_upload）；需要附件原文、没附全的页或之前发过的附件时用 read_attachment。",
    "7. 时间表只有日程一种，要孩子做的事都用 create_events 建日程，需要提醒时在日程里带 reminder。按 time_mode 区分：课程、兴趣班用 range（时段）；作业「几点前做完」用 due（截止）；喝水、收书包用 point（到点）；生日、放假用 all_day（全天）。要孩子做 App 里的学习内容时带 action={type:\"content\",content_id}（可重复，每天单独记完成）；要打开其它 App 时带 action={type:\"app\",name,ios_url,android_package}。",
    "8. 安排今天的事或设提醒前先确认现在几点（上面给出了现在的时间，不确定时调用 get_now），不要安排在已经过去的时间；家长说的时间已经过了，先问一句是改到稍后还是明天。",
  ].join("\n");
}

// ---------- 工具 ----------

/** MCP 工具列表没取到时只有本地工具，下一轮再试 */
let toolsDegraded = false;

/** 取工具列表（MCP + 本地的 search_history）；MCP 失败时返回错误说明，只用本地工具继续 */
async function ensureTools(signal?: AbortSignal): Promise<string | null> {
  if (toolSpecs.length && !toolsDegraded) return null;
  let list: ToolDef[] = [];
  let failure: string | null = null;
  try {
    list = (await mcp.loadTools(signal)).map((t) => ({
      name: t.name,
      title: t.title,
      description: t.description,
      parameters: t.inputSchema,
      readOnly: t.readOnly,
      destructive: t.destructive,
    }));
  } catch (e) {
    if (isAbort(e)) throw e;
    failure = errorText(e);
    mcp = new McpClient();
  }
  toolsDegraded = failure != null;
  if (!list.some((t) => t.name === SEARCH_TOOL.name)) list.push(SEARCH_TOOL);
  if (!list.some((t) => t.name === NOW_TOOL.name)) list.push(NOW_TOOL);
  if (!list.some((t) => t.name === READ_ATTACHMENT_TOOL.name)) list.push(READ_ATTACHMENT_TOOL);
  tools = new Map(list.map((t) => [t.name, t]));
  toolSpecs = list.map((t) => ({ type: "function", function: { name: t.name, description: t.description, parameters: t.parameters } }));
  return failure;
}

async function searchHistory(args: Record<string, unknown>, signal?: AbortSignal): Promise<{ text: string; isError: boolean }> {
  const q = String(args.query ?? "").trim();
  if (!q) return { text: '{"error":"缺少 query"}', isError: true };
  const limit = Math.min(Math.max(Number(args.limit) || 5, 1), 20);
  try {
    const r = await api.get("assistant/search", { q, limit }, { raw: true, signal });
    return { text: JSON.stringify(r), isError: false };
  } catch (e) {
    if (isAbort(e)) throw e;
    return { text: JSON.stringify({ error: errorText(e) }), isError: true };
  }
}

/** 模型刚说的话（"明天下午四点的钢琴课，我这就删掉"）一般就是对这次操作的说明，比参数更好懂 */
function lastSaid(): string | null {
  const items = agentStore.getState().items;
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i]!;
    if (it.kind === "user") break;
    if (it.kind === "assistant" && it.text.trim()) return it.text.trim();
  }
  return null;
}

async function runTool(call: ToolCall, signal: AbortSignal): Promise<{ text: string; isError: boolean }> {
  const info = tools.get(call.name);
  const title = info?.title ?? call.name;
  const itemId = append({ kind: "tool", title, state: "running" });
  const finish = (state: ToolState) => patchItem(itemId, { state } as Partial<Item>);

  let args: Record<string, unknown> = {};
  if (call.arguments.trim()) {
    try {
      const parsed: unknown = JSON.parse(call.arguments);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("not object");
      args = parsed as Record<string, unknown>;
    } catch {
      finish("failed");
      return { text: "参数不是合法的 JSON，请重新生成参数。", isError: true };
    }
  }
  if (info?.destructive) {
    const ok = await confirm({ title: `确认${title}？`, message: lastSaid() ?? describeArgs(args), confirmText: "确认执行", danger: true });
    if (signal.aborted) {
      finish("cancelled");
      throw new DOMException("已停止", "AbortError");
    }
    if (!ok) {
      finish("cancelled");
      return { text: "家长取消了这个操作，没有执行。请不要重试，直接告诉家长已取消。", isError: true };
    }
  }
  try {
    const r =
      call.name === NOW_TOOL.name
        ? nowResult()
        : call.name === SEARCH_TOOL.name
          ? await searchHistory(args, signal)
          : call.name === READ_ATTACHMENT_TOOL.name
            ? await readAttachment(args, readDeps)
            : await mcp.callTool(call.name, args, signal);
    finish(r.isError ? "failed" : "done");
    return { text: r.text.slice(0, MAX_TOOL_RESULT), isError: r.isError };
  } catch (e) {
    if (isAbort(e)) {
      finish("cancelled");
      throw e;
    }
    finish("failed");
    return { text: `调用失败：${errorText(e)}`, isError: true };
  }
}

// ---------- 发过的附件（read_attachment） ----------

/** 家长在对话里发过的附件（有附件 id 的）：asset_id → 附件信息 + 发送时间 */
let attachmentLog = new Map<string, SentAttachment>();

function noteAttachments(meta: AttachmentMeta[] | null | undefined, sentAt: number): void {
  for (const a of meta ?? []) {
    if (!a?.asset_id) continue;
    attachmentLog.set(a.asset_id, {
      asset_id: a.asset_id,
      name: a.name,
      kind: a.kind,
      ...(a.mime ? { mime: a.mime } : {}),
      ...(a.size != null ? { size: a.size } : {}),
      ...(a.page_count != null ? { page_count: a.page_count } : {}),
      sent_at: sentAt,
    });
  }
}

function noteAttachmentsIn(messages: ServerMessage[]): void {
  for (const m of messages) if (m.role === "user" && m.attachments?.length) noteAttachments(m.attachments, m.created_at ?? 0);
}

/**
 * 家长最近在对话里发过的附件，最新的在前：界面上加载过的记录和这次会话发的，另外从服务端再取最近 200 条消息
 * （更早的附件可以用 search_history 搜文件名，消息里带附件 id；与 iOS VoiceAgent.sentAttachments 一致）
 */
async function sentAttachments(limit = 20): Promise<SentAttachment[]> {
  try {
    const r = await api.get<{ messages: ServerMessage[] }>("assistant/conversation", { limit: 200 }, { raw: true });
    noteAttachmentsIn(r.messages ?? []);
  } catch (e) {
    console.warn("[assistant] 读取附件记录失败", errorText(e));
  }
  return [...attachmentLog.values()].sort((a, b) => b.sent_at - a.sent_at).slice(0, limit);
}

/** read_attachment 的依赖：附件信息来自对话记录或同步下来的附件表，下载用签名地址 */
const readDeps: ReadDeps = {
  listSent: () => sentAttachments(),
  async info(id) {
    const s = attachmentLog.get(id);
    if (s) return { name: s.name, mime: s.mime ?? "", ...(s.size != null ? { size: s.size } : {}) };
    const fromAssets = () => {
      const a = dataStore.getState().assets[id];
      return a ? { name: a.fileName, mime: a.mime, size: a.size } : null;
    };
    // 刚上传的附件可能还没同步下来
    if (!fromAssets()) await syncNow().catch(() => undefined);
    return fromAssets();
  },
  async download(id) {
    const url = await assetUrl(id);
    if (!url) throw new Error("网页版不能读取内置资源");
    const res = await fetch(url);
    if (!res.ok) throw new Error(`下载失败（${res.status}）`);
    return res.arrayBuffer();
  },
  async openPdf(data) {
    const doc = await openPdfData(data);
    if (!doc) return null;
    return { numPages: doc.numPages, pageText: (n) => pdfPageText(doc, n), close: () => void doc.loadingTask.destroy() };
  },
  formatTime(ms) {
    const cal = calendarNow();
    return `${cal.dateString(ms)} ${cal.timeString(ms)}`;
  },
};

// ---------- 上下文 ----------

/** 记一条助手 / 工具消息：放进上下文，并排队保存到服务端 */
function record(msg: ServerMessage): void {
  const id = uid();
  const json: ChatMessage = { role: msg.role, content: msg.content ?? "" };
  if (msg.tool_calls?.length) json.tool_calls = msg.tool_calls;
  if (msg.role === "tool") json.tool_call_id = msg.tool_call_id ?? "";
  context.push({ id, seq: null, tokens: messageTokens(msg.content ?? "", msg.tool_calls), json, reasoning: msg.reasoning ?? null });
  unsaved.push({ ...msg, id, created_at: Date.now() });
}

/** 停止时，补上还没有结果的工具调用（否则下一轮请求会因为工具调用没有结果而报错） */
function closeDanglingToolCalls(): void {
  for (let i = context.length - 1; i >= 0; i--) {
    const m = context[i]!;
    if (m.json.role === "user") return;
    if (m.json.role !== "assistant") continue;
    const calls = (m.json.tool_calls as ToolCallJSON[] | undefined) ?? [];
    const answered = new Set(context.slice(i + 1).filter((x) => x.json.role === "tool").map((x) => x.json.tool_call_id as string));
    for (const c of calls) {
      if (!answered.has(c.id)) record({ id: "", role: "tool", tool_call_id: c.id, name: c.function.name, content: "家长停止了这次操作，没有执行。" });
    }
    return;
  }
}

/** 上下文只保留预算内的最近消息，在用户消息处截断 */
function trimContext(budget: number): void {
  const keep = keepFrom(
    context.map((m) => m.json.role),
    context.map((m) => m.tokens),
    budget,
    MAX_HISTORY,
  );
  if (keep > 0 && keep < context.length) context = context.slice(keep);
}

/** 这一轮结束后，上下文里的图片换成文字占位（后面的轮次可能用不能看图的模型） */
function dropImages(userId: string, text: string): void {
  const m = context.find((x) => x.id === userId);
  if (m && Array.isArray(m.json.content)) m.json = { ...m.json, content: text };
}

// ---------- 会话历史 ----------

interface ConversationMeta {
  summary?: string | null;
  summary_upto_seq?: number;
  message_count?: number;
}

/** 服务端消息 → 界面条目 */
function itemsFor(m: ServerMessage): Item[] {
  const content = (m.content ?? "").trim();
  if (m.role === "user") return [{ id: uid(), kind: "user", text: displayText(content, m.attachments) }];
  if (m.role !== "assistant") return [];
  const out: Item[] = [];
  const r = (m.reasoning ?? "").trim();
  if (r) out.push({ id: uid(), kind: "thinking", text: r });
  if (content) out.push({ id: uid(), kind: "assistant", text: content });
  for (const c of m.tool_calls ?? []) {
    const name = c?.function?.name ?? "工具";
    out.push({ id: uid(), kind: "tool", title: tools.get(name)?.title ?? name, state: "done" });
  }
  return out;
}

/** 从服务端加载会话：最近 60 条显示在界面上，摘要之后的消息按预算放进上下文 */
export async function loadHistory(): Promise<void> {
  const st = agentStore.getState();
  if (st.historyLoaded || st.loadingHistory) return;
  const gen = generation;
  agentStore.setState({ loadingHistory: true });
  try {
    const r = await api.get<{ conversation: ConversationMeta; messages: ServerMessage[] }>("assistant/conversation", { limit: 60 }, { raw: true });
    const failure = await ensureTools();
    if (failure) console.warn("[assistant] 获取工具列表失败", failure);
    if (gen !== generation) return;
    const messages = r.messages ?? [];
    summary = r.conversation?.summary ?? null;
    summaryUptoSeq = r.conversation?.summary_upto_seq ?? 0;
    oldestSeq = messages[0]?.seq ?? null;
    noteAttachmentsIn(messages);
    context = messages
      .map(toContextMessage)
      .filter((m): m is ContextMessage => m != null)
      .filter((m) => (m.seq ?? 0) > summaryUptoSeq);
    trimContext(contextBudget(prefsStore.getState().model));
    // 加载期间已经开始的对话（条目）排在历史后面
    agentStore.setState((s) => ({ items: [...messages.flatMap(itemsFor), ...s.items], hasOlder: (oldestSeq ?? 1) > 1, historyLoaded: true }));
  } catch (e) {
    console.warn("[assistant] 加载会话历史失败", errorText(e));
  } finally {
    if (gen === generation) agentStore.setState({ loadingHistory: false });
  }
}

/** 往前翻更早的记录（只影响界面） */
export async function loadOlder(): Promise<void> {
  const before = oldestSeq;
  if (before == null || before <= 1 || agentStore.getState().loadingOlder) return;
  const gen = generation;
  agentStore.setState({ loadingOlder: true });
  try {
    const r = await api.get<{ messages: ServerMessage[] }>("assistant/messages", { before_seq: before, limit: 50 }, { raw: true });
    if (gen !== generation) return;
    const messages = r.messages ?? [];
    noteAttachmentsIn(messages);
    oldestSeq = messages[0]?.seq ?? before;
    agentStore.setState((s) => ({ items: [...messages.flatMap(itemsFor), ...s.items], hasOlder: (oldestSeq ?? 1) > 1 && messages.length > 0 }));
  } catch (e) {
    append({ kind: "error", text: `加载更早的记录失败：${errorText(e)}` });
  } finally {
    if (gen === generation) agentStore.setState({ loadingOlder: false });
  }
}

/** 保存这一轮的新消息；后台压缩完成时更新摘要，并从上下文去掉已被摘要覆盖的消息 */
async function flush(): Promise<void> {
  if (!unsaved.length || flushing) return;
  flushing = true;
  const gen = generation;
  const batch = unsaved.slice(0, 100);
  try {
    const r = await api.post<{ conversation: ConversationMeta; seqs: Record<string, number> }>("assistant/messages", { messages: batch }, { raw: true });
    if (gen !== generation) return;
    unsaved = unsaved.slice(batch.length);
    for (const m of context) if (m.seq == null && r.seqs?.[m.id] != null) m.seq = r.seqs[m.id]!;
    const upto = r.conversation?.summary_upto_seq ?? 0;
    if (upto > summaryUptoSeq) {
      summary = r.conversation?.summary ?? null;
      summaryUptoSeq = upto;
      context = context.filter((m) => (m.seq ?? Number.MAX_SAFE_INTEGER) > upto);
    }
  } catch (e) {
    console.warn("[assistant] 保存会话历史失败，下一轮再试", errorText(e));
  } finally {
    flushing = false;
  }
  if (unsaved.length && gen === generation && !flushing) void flush();
}

// ---------- 发送 ----------

/**
 * 把附件原件上传到附件存储。失败时重试两次（网络抖动）；最后还是失败就照样发送，
 * 但标上 uploadFailed（消息里告诉模型没有附件 id）并提示家长（与 iOS ParentAssistant.upload 一致）
 */
async function uploadAttachments(list: PreparedAttachment[]): Promise<PreparedAttachment[]> {
  agentStore.setState({ uploading: true });
  try {
    return await Promise.all(
      list.map(async (a) => {
        try {
          const asset = await uploadWithRetry(a.uploadFile);
          return { ...a, assetId: asset.id };
        } catch (e) {
          console.warn("[assistant] 附件上传失败", errorText(e));
          toast(`「${a.name}」上传失败：${errorText(e)}`, { error: true });
          return { ...a, uploadFailed: true };
        }
      }),
    );
  } finally {
    agentStore.setState({ uploading: false });
  }
}

/** 最多试 3 次（间隔 1、2 秒）；4xx（参数不对、没有权限）不重试 */
async function uploadWithRetry(file: File): Promise<Awaited<ReturnType<typeof upload>>> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await upload(file);
    } catch (e) {
      const clientError = e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429;
      if (attempt >= 3 || clientError) throw e;
      console.warn(`[assistant] 附件上传第 ${attempt} 次失败，重试`, errorText(e));
      await new Promise((r) => setTimeout(r, attempt * 1000));
    }
  }
}

/** 发送一句话（可以带图片、文件） */
export function send(text: string, attachments: PreparedAttachment[] = []): void {
  const t = text.trim();
  if (!t && !attachments.length) return;
  stop();
  const ctrl = new AbortController();
  abort = ctrl;
  agentStore.setState({ busy: true });
  void submit(t, attachments, ctrl).finally(() => {
    if (abort === ctrl) {
      abort = null;
      agentStore.setState({ busy: false, liveThinkingId: null });
    }
  });
}

async function submit(text: string, rawAttachments: PreparedAttachment[], ctrl: AbortController): Promise<void> {
  const signal = ctrl.signal;
  const gen = generation;
  let creds = credStore.getState().creds;
  if (!creds) creds = await refreshCredentials();
  if (signal.aborted || gen !== generation) return;
  if (!creds) {
    append({ kind: "error", text: MISSING_KEY });
    return;
  }
  const attachments = rawAttachments.length ? await uploadAttachments(rawAttachments) : [];
  if (signal.aborted || gen !== generation) return;

  // 保存的文字：附件的文字内容拼在后面（可以全文检索），图片只留占位；标题里带附件 id
  const saved = savedUserText(
    text,
    attachments.map((a) => ({ name: a.name, text: a.text, imageCount: a.imageDataUrls.length, assetId: a.assetId, uploadFailed: a.uploadFailed, pageCount: a.pageCount, textPages: a.textPages })),
  );
  const images = attachments.flatMap((a) => a.imageDataUrls);
  const meta: AttachmentMeta[] = attachments.map((a) => ({
    kind: a.kind,
    name: a.name,
    mime: a.mime,
    size: a.size,
    ...(a.assetId ? { asset_id: a.assetId } : {}),
    ...(a.pageCount != null ? { page_count: a.pageCount } : {}),
  }));
  append({ kind: "user", text: displayText(saved, meta) });
  noteAttachments(meta, Date.now());
  const userId = uid();
  const modelContent: unknown = images.length
    ? [{ type: "text", text: saved || "请看图片" }, ...images.map((url) => ({ type: "image_url", image_url: { url } }))]
    : saved;
  context.push({ id: userId, seq: null, tokens: messageTokens(saved), json: { role: "user", content: modelContent } });
  unsaved.push({ id: userId, role: "user", content: saved, created_at: Date.now(), ...(meta.length ? { attachments: meta } : {}) });

  // 对话服务：有图片而选的模型不能看图时，这一轮改用百炼的看图模型
  const prefs = prefsStore.getState();
  const route = chatRoute(prefs, creds);
  const vision = visionRoute(route.provider, route.model, images.length > 0);
  const provider = vision.provider;
  const model = vision.model;
  const chatKey = vision.switched ? creds.apiKey : route.key;
  if (route.customMissing && !vision.switched) {
    append({ kind: "error", text: "自定义对话服务还没有配置好，请在「助手设置」里检查。" });
    void flush();
    return;
  }
  if (!chatKey) {
    append({ kind: "error", text: `还没有设置${provider.name}的 API Key，请在「助手设置」里设置。` });
    void flush();
    return;
  }
  trimContext(contextBudget(model));
  const thinkingLevel = supportsThinking(provider, model) && prefs.thinking !== "off" ? prefs.thinking : null;
  let wrote = false;
  try {
    const failure = await ensureTools(signal);
    if (failure) append({ kind: "error", text: `家庭数据工具暂时不可用（${failure}），这次只能回答不需要查数据的问题。` });
    let finished = false;
    for (let step = 0; step < MAX_STEPS; step++) {
      if (signal.aborted) throw new DOMException("已停止", "AbortError");
      // 通义千问只在每轮的第一次请求思考（拿到工具结果后它会把同样的思考从头再来一遍）；DeepSeek 每一步都思考
      const thinking = step === 0 || thinksEveryStep(provider) ? thinkingLevel : null;
      let bubble: string | null = null;
      let thought: string | null = null;
      let bubbleText = "";
      let thoughtText = "";
      const system: ChatMessage = { role: "system", content: withSummary(systemPrompt(), summary) };
      const turn = await streamChat({
        provider,
        apiKey: chatKey,
        model,
        messages: [system, ...requestMessages(context, provider, thinking != null)],
        tools: toolSpecs,
        thinking: thinking ?? "off",
        signal,
        onReasoning: (d) => {
          thoughtText += d;
          if (!thought) {
            thought = append({ kind: "thinking", text: thoughtText });
            agentStore.setState({ liveThinkingId: thought });
          } else patchItem(thought, { text: thoughtText });
        },
        onText: (d) => {
          bubbleText += d;
          if (thought && agentStore.getState().liveThinkingId === thought) agentStore.setState({ liveThinkingId: null });
          if (!bubble) bubble = append({ kind: "assistant", text: bubbleText });
          else patchItem(bubble, { text: bubbleText });
        },
      });
      if (agentStore.getState().liveThinkingId) agentStore.setState({ liveThinkingId: null });
      const msg: ServerMessage = { id: "", role: "assistant", content: turn.content };
      if (turn.reasoning) msg.reasoning = turn.reasoning;
      if (turn.toolCalls.length) msg.tool_calls = turn.toolCalls.map((c) => ({ id: c.id, type: "function", function: { name: c.name, arguments: c.arguments } }));
      record(msg);
      if (!turn.toolCalls.length) {
        finished = true;
        break;
      }
      for (const call of turn.toolCalls) {
        if (signal.aborted) throw new DOMException("已停止", "AbortError");
        const result = await runTool(call, signal);
        if (!(tools.get(call.name)?.readOnly ?? true) && !result.isError) wrote = true;
        record({ id: "", role: "tool", tool_call_id: call.id, name: call.name, content: result.text });
      }
    }
    if (!finished) append({ kind: "error", text: "步骤太多，先停在这里了。可以换个说法再试。" });
  } catch (e) {
    if (gen !== generation) return;
    closeDanglingToolCalls();
    if (!isAbort(e)) append({ kind: "error", text: errorText(e) });
  } finally {
    if (gen === generation) {
      if (wrote) syncSoon();
      dropImages(userId, saved);
      void flush();
    }
  }
}

/** 停止当前处理（关闭正在等待的确认框） */
export function stop(): void {
  if (!abort) return;
  abort.abort();
  abort = null;
  answerConfirm(false);
  agentStore.setState({ busy: false, liveThinkingId: null });
}

/** 清空本地状态（退出登录、清空记录后；服务端的历史不受影响） */
export function reset(): void {
  stop();
  generation++;
  mcp = new McpClient();
  tools = new Map();
  toolSpecs = [];
  toolsDegraded = false;
  context = [];
  unsaved = [];
  summary = null;
  summaryUptoSeq = 0;
  oldestSeq = null;
  flushing = false;
  attachmentLog = new Map();
  agentStore.setState(initialState());
}

/** 清空家长端自己的对话记录，或某个孩子平板上的对话记录 */
export async function clearConversation(childId?: string): Promise<void> {
  await api.del("assistant/conversation", childId ? { query: { child_id: childId } } : undefined);
  if (!childId) {
    reset();
    await loadHistory();
  }
}

// 退出登录：清空对话和内存里的 Key
let lastPhase = sessionStore.getState().phase;
sessionStore.subscribe(() => {
  const phase = sessionStore.getState().phase;
  if (phase === lastPhase) return;
  lastPhase = phase;
  if (phase === "login") {
    reset();
    clearCredentials();
  }
});
